import React, { useState, createContext, useContext, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config"; // Make sure this import exists or adjust accordingly
import { useTranslation } from 'react-i18next';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Use sessionStorage instead of localStorage to clear session when browser closes
    const storedUser = sessionStorage.getItem("user");
    // No logging of stored user
    
    try {
      const user = storedUser ? JSON.parse(storedUser) : null;
      const timestamp = sessionStorage.getItem("timestamp");
  
      const now = new Date().getTime();
      // Reduce session timeout to 4 hours and clear when browser closes
      const isExpired = !timestamp || now - timestamp > 4 * 60 * 60 * 1000;
  
      if (isExpired) {
        console.log("Auth session expired, clearing storage");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("timestamp");
        sessionStorage.removeItem("settingsSynced");
        return null;
      }
  
      // No logging of authenticated user data
      return user;
    } catch (error) {
      // console.error("Error parsing stored user:", error);
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("timestamp");
      sessionStorage.removeItem("settingsSynced");
      return null;
    }
  });

  const { i18n } = useTranslation();

  const syncUserSettings = async (authToken) => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/system-settings?format=object&noLimit=true`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const settings = resp.data?.data || resp.data;
      if (settings) {
        // Theme - keep in localStorage as this should persist across sessions
        const shouldBeDark = ['dark', 'true', true, 1, '1'].includes(settings.dark_mode);
        localStorage.setItem('theme', shouldBeDark ? 'dark' : 'light');
        // Language - keep in localStorage as this should persist across sessions
        if (settings.language) {
          localStorage.setItem('i18nextLng', settings.language);
          if (settings.language !== i18n.language) {
            i18n.changeLanguage(settings.language);
          }
        }
      }
    } catch (e) {
      console.error('Failed to sync user settings', e);
    }
  };

  useEffect(() => {
    if (user) {
      // Don't log user data to console
      sessionStorage.setItem("user", JSON.stringify(user));
      sessionStorage.setItem("timestamp", new Date().getTime());
      if (user.token && !sessionStorage.getItem('settingsSynced')) {
        syncUserSettings(user.token).finally(()=>{
          sessionStorage.setItem('settingsSynced','true');
        });
      }
    } else {
      console.log('User logged out, removing from sessionStorage');
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("timestamp");
      sessionStorage.removeItem("settingsSynced");
    }
  }, [user]);

  // Function to fetch complete user profile data
  const fetchUserProfile = async (userId, token) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const login = async (userData) => {
    // Remove console log that exposes sensitive information
    if (!userData) {
      // console.error('Login called with invalid userData:', userData);
      return;
    }
    
    // Make sure token is included in user data for consistent storage
    if (userData.token && userData.user) {
      // Store the basic user data first
      const basicUserData = {
        ...userData.user,
        token: userData.token
      };
      
      try {
        // Fetch complete user profile
        const fullUserProfile = await fetchUserProfile(basicUserData.id, userData.token);
        
        if (fullUserProfile) {
          // If we got the full profile, merge it with the token
          const enhancedUserData = {
            ...fullUserProfile,
            token: userData.token
          };
          setUser(enhancedUserData);
        } else {
          // If profile fetch failed, fall back to basic data
          setUser(basicUserData);
        }
      } catch (error) {
        // In case of error, still set the basic user data
        setUser(basicUserData);
      }
    } else {
      setUser(userData);
    }
  };

  const logout = () => {
    console.log('Logout action initiated');
    setUser(null);
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("timestamp");
    sessionStorage.removeItem("settingsSynced");
  };

  // Helper function to check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user || !user.permissions) {
      return false;
    }
    return user.permissions.includes(permission);
  };

  // Add computed isAuthenticated property
  const isAuthenticated = !!user;
  // No logging of auth state

  // Expose token directly for easier access
  const token = user?.token;

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
